import { ElementType, forwardRef, HTMLProps, ReactNode } from "react"
import styled from "styled-components"
import { Typography } from "../../../typography"

const RadioInput = styled.input`
  appearance: none;
  margin: 0;
  position: relative;
  color: ${({ theme }) => theme.colors.grey.$600};
  height: 2.5rem;
  display: flex;
  align-items: center;

  &::before {
    content: "";
    display: block;
    width: 0.875rem;
    height: 0.875rem;
    border: 1.5px solid ${({ theme }) => theme.colors.grey.$600};
    border-radius: 50%;
    box-sizing: border-box;
  }

  &::after {
    content: "";
    display: block;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    transition: 120ms transform ease-in-out;

    background-color: ${({ theme }) => theme.colors.grey.$600};
    transform: scale(0);
    position: absolute;
    top: 1rem;
    left: 0.2rem;
  }

  &:checked::after {
    transform: scale(1);
  }

  &:disabled::before {
    border-color: ${({ theme }) => theme.colors.grey.$400};
  }
  &:disabled::after {
    background-color: ${({ theme }) => theme.colors.grey.$400};
  }

  &:focus {
    outline: none;
  }
`

const RadioLabel = styled(Typography).attrs(() => ({
  $variant: "body-4-medium",
}))`
  color: ${({ theme }) => theme.colors.grey.$600};

  height: 2.5rem;
  display: inline-flex;
  align-items: center;

  position: relative;

  cursor: pointer;

  padding: 0 0 0 1.5rem;
  margin-left: -1rem;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;

  transition: all 0.2s ease;
  box-sizing: border-box;
  border: 1px solid transparent;

  ${RadioInput}:focus + & {
    outline: 2px solid ${({ theme }) => theme.colors.grey.$400};
    outline-left: none;
  }

  ${RadioInput}:disabled + & {
    color: ${({ theme }) => theme.colors.grey.$400};
  }

  ${RadioInput}:disabled + &::before {
    background-color: ${({ theme }) => theme.colors.grey.$400};
  }
`

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
`

export type CheckboxProps = {
  value: string
  text: ReactNode
  id: string
  labelProps?: HTMLProps<HTMLLabelElement> & { as?: ElementType }
  containerProps?: HTMLProps<HTMLLabelElement> & { as?: ElementType }
  as?: ElementType
} & HTMLProps<HTMLInputElement>

const RadioCmp = (
  {
    value,
    text,
    id,
    labelProps,
    containerProps,
    as,
    ...props
  }: {
    value: string
    text: ReactNode
    id: string
    labelProps?: HTMLProps<HTMLLabelElement> & { as?: ElementType }
    containerProps?: HTMLProps<HTMLLabelElement> & { as?: ElementType }
    as?: ElementType
  } & HTMLProps<HTMLInputElement>,
  ref?: React.ForwardedRef<HTMLInputElement>,
) => {
  return (
    <RadioContainer {...containerProps}>
      <RadioInput id={id} type="radio" value={value} {...props} ref={ref} />
      <RadioLabel htmlFor={id} as="label" {...labelProps}>
        {text}
      </RadioLabel>
    </RadioContainer>
  )
}

export const Radio = forwardRef(RadioCmp) as (
  props: CheckboxProps & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof RadioCmp>
